/**
 * TimelineTab — proves the stage-transition recorder is alive, and shows
 * what it decided, with evidence.
 *
 * An empty event list is indistinguishable from a dead recorder — that
 * ambiguity is exactly how an empty Pipeline Event Log went unnoticed for
 * months (see PipelineWritesTab). The liveness strip + coverage % below are
 * the point of this tab; the per-task event list is secondary.
 *
 * Takes `{ canEdit }` to keep the tab signature uniform with its siblings
 * (mirrors the Memory dashboard's prop threading), even though this tab is
 * currently read-only.
 */

import {
  Alert,
  Box,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { useState, type FormEvent, type ReactElement } from 'react';

import { usePmTimeline } from '@/hooks/usePmTimeline';
import type { StageEvent, StageEventSource, TimelineHealth } from '@/types/pm_stage_event';

interface TimelineTabProps {
  canEdit: boolean;
}

// ---------------------------------------------------------------------------
// Freshness — the one rule that matters on this page.
// ---------------------------------------------------------------------------

const STALE_AFTER_MS = 30 * 60 * 1000;

type Freshness = 'never' | 'stale' | 'fresh';

function freshness(iso: string | null): Freshness {
  if (!iso) return 'never';
  return Date.now() - new Date(iso).getTime() > STALE_AFTER_MS ? 'stale' : 'fresh';
}

const FRESHNESS_COLOR: Record<Freshness, string> = {
  never: 'error.main',
  stale: 'warning.main',
  fresh: 'text.primary',
};

// Each source's count and its liveness timestamp are the same three fields —
// reused for both the liveness strip and the source breakdown chips.
const SOURCES: Array<{ key: StageEventSource; label: string; lastAtKey: keyof TimelineHealth }> = [
  { key: 'poll', label: 'Last poll', lastAtKey: 'last_poll_at' },
  { key: 'github', label: 'Last GitHub event', lastAtKey: 'last_github_event_at' },
  { key: 'notion_webhook', label: 'Last Notion event', lastAtKey: 'last_notion_event_at' },
];

// ---------------------------------------------------------------------------
// Liveness strip
// ---------------------------------------------------------------------------

function StatTile({ label, value, color }: { label: string; value: string; color?: string }): ReactElement {
  return (
    <Box sx={{ minWidth: 160, flex: 1 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6" sx={{ color: color ?? 'text.primary' }}>
        {value}
      </Typography>
    </Box>
  );
}

function LivenessStrip({ health }: { health: TimelineHealth }): ReactElement {
  return (
    <Box>
      <Stack direction="row" spacing={3} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        {SOURCES.map(({ key, label, lastAtKey }) => {
          const iso = health[lastAtKey] as string | null;
          const state = freshness(iso);
          return (
            <StatTile
              key={key}
              label={label}
              value={state === 'never' ? 'never' : new Date(iso as string).toLocaleString()}
              color={FRESHNESS_COLOR[state]}
            />
          );
        })}
        <StatTile label="Coverage" value={`${health.coverage_pct}%`} />
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {health.tasks_with_timeline}/{health.tasks_total} live tasks have a recorded timeline ·{' '}
        {health.events_24h} events in the last 24h
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Source breakdown
// ---------------------------------------------------------------------------

function SourceBreakdown({ health }: { health: TimelineHealth }): ReactElement {
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {SOURCES.map(({ key, label, lastAtKey }) => {
        const state = freshness(health[lastAtKey] as string | null);
        return (
          <Chip
            key={key}
            size="small"
            label={`${key}: ${health.source_counts[key]}`}
            color={state === 'never' ? 'error' : state === 'stale' ? 'warning' : 'default'}
            variant={state === 'fresh' ? 'outlined' : 'filled'}
            title={label}
          />
        );
      })}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Evidence — rendered as a human sentence, not raw JSON.
// ---------------------------------------------------------------------------

function evidenceSentence(event: StageEvent): string {
  const { source, evidence, to_stage } = event;
  if (!evidence) return `${source} event`;

  if (source === 'github') {
    const prNumber = evidence.pr_number;
    const verb = to_stage.toLowerCase() === 'done' ? 'merged' : 'opened';
    return typeof prNumber === 'number' || typeof prNumber === 'string'
      ? `PR #${prNumber} ${verb}`
      : `GitHub PR ${verb}`;
  }

  if (source === 'notion_webhook') {
    const via = evidence.via;
    return typeof via === 'string' ? `Notion webhook (${via})` : 'Notion webhook';
  }

  // poll — two shapes: task-pipeline derivation, or a raw ticket status read.
  if (typeof evidence.rule === 'string' && typeof evidence.raw_pipeline === 'string') {
    return `Poll derived from tickets (raw Pipeline: "${evidence.raw_pipeline}")`;
  }
  if (typeof evidence.raw_status === 'string') {
    return `Poll read Notion Status "${evidence.raw_status}"`;
  }
  return 'Poll detected a change';
}

// ---------------------------------------------------------------------------
// Task lookup + per-task timeline
// ---------------------------------------------------------------------------

const SOURCE_CHIP_COLOR: Record<StageEventSource, 'default' | 'info' | 'secondary'> = {
  github: 'secondary',
  notion_webhook: 'info',
  poll: 'default',
};

function EventRow({ event }: { event: StageEvent }): ReactElement {
  return (
    <TableRow>
      <TableCell>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
          {new Date(event.occurred_at).toLocaleString()}
        </Typography>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {event.from_stage ?? '(unset)'}
          </Typography>
          <ArrowForwardIcon fontSize="small" sx={{ color: 'text.disabled' }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {event.to_stage}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell>
        <Chip size="small" label={event.source} color={SOURCE_CHIP_COLOR[event.source]} />
      </TableCell>
      <TableCell>
        <Typography variant="body2">{evidenceSentence(event)}</Typography>
      </TableCell>
    </TableRow>
  );
}

function TaskLookup({
  timeline,
  isLoading,
  error,
  loadTimeline,
}: {
  timeline: ReturnType<typeof usePmTimeline>['timeline'];
  isLoading: boolean;
  error: string | null;
  loadTimeline: (notionId: string) => Promise<void>;
}): ReactElement {
  const [notionId, setNotionId] = useState('');
  // Session-only — pages looked up this visit, for one-click re-lookup.
  const [recent, setRecent] = useState<Array<{ notion_id: string; title: string | null }>>([]);

  const runLookup = (id: string): void => {
    const trimmed = id.trim();
    if (!trimmed) return;
    void loadTimeline(trimmed).then(() => {
      setRecent((prev) => [
        { notion_id: trimmed, title: null },
        ...prev.filter((r) => r.notion_id !== trimmed),
      ].slice(0, 5));
    });
  };

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    runLookup(notionId);
  };

  // Recent-list entries pick up the title once we have it for that page.
  const recentWithTitles = recent.map((r) =>
    r.notion_id === timeline?.notion_id && timeline.title ? { ...r, title: timeline.title } : r,
  );

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Task lookup
      </Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <TextField
            id="timeline-notion-id"
            label="Notion page id"
            size="small"
            value={notionId}
            onChange={(e) => setNotionId(e.target.value)}
            sx={{ minWidth: 320 }}
          />
          <IconButton type="submit" size="large" aria-label="Load timeline">
            <SearchIcon />
          </IconButton>
        </Stack>
      </Box>

      {recentWithTitles.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
            Recent:
          </Typography>
          {recentWithTitles.map((r) => (
            <Chip
              key={r.notion_id}
              size="small"
              variant="outlined"
              label={r.title ?? r.notion_id}
              onClick={() => runLookup(r.notion_id)}
            />
          ))}
        </Stack>
      )}

      {error && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isLoading && (
        <Box role="status" aria-label="Loading task timeline">
          <Skeleton variant="rectangular" height={40} sx={{ mb: 0.5 }} />
          <Skeleton variant="rectangular" height={40} sx={{ mb: 0.5 }} />
        </Box>
      )}

      {!isLoading && !error && timeline && (
        <>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {timeline.title ?? '(untitled)'} — current stage: {timeline.current_stage ?? 'unknown'}
          </Typography>
          {timeline.events.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No transitions recorded for this page yet.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small" aria-label="Task stage timeline">
                <TableHead>
                  <TableRow>
                    <TableCell>Occurred</TableCell>
                    <TableCell>Stage change</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Evidence</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {timeline.events.map((event) => (
                    <EventRow key={event.id} event={event} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Tab root
// ---------------------------------------------------------------------------

export function TimelineTab(_props: TimelineTabProps): ReactElement {
  const { health, timeline, isLoading, error, loadTimeline, refetch } = usePmTimeline();

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end">
        <IconButton size="large" aria-label="Refresh recorder health" onClick={refetch}>
          <RefreshIcon />
        </IconButton>
      </Stack>

      {error && !health && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isLoading && !health && (
        <Box role="status" aria-label="Loading recorder health">
          <Skeleton variant="rectangular" height={72} sx={{ mb: 2 }} />
        </Box>
      )}

      {health && (
        <>
          <LivenessStrip health={health} />
          <Box sx={{ mt: 2, mb: 3 }}>
            <SourceBreakdown health={health} />
          </Box>
        </>
      )}

      <TaskLookup
        timeline={timeline}
        isLoading={isLoading && Boolean(health)}
        error={health ? error : null}
        loadTimeline={loadTimeline}
      />
    </Box>
  );
}
